import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Spinner } from "react-bootstrap";
import Select from "../../Common/FormElements/Select/Select";
import Button from "../../Common/FormElements/Button/Button";
import QuotePill, {
  failureLabel,
  formatEuro,
  unitLabel,
  unitTone,
} from "./QuotePill";
import EditQuoteModal from "./EditQuoteModal";
import AppCustomerModal from "../AppCustomerModal";
import {
  createQuoteOrder,
  fetchCustomerRow,
  fetchQuoteOrderPreview,
} from "../../../REDUX/actions/appProjects";
import { switchCarts } from "../../../REDUX/actions/catalogue";
import {
  closeCenteredModal,
  openCenteredModal,
} from "../../../REDUX/actions/centeredModal";
import {
  closeRightModal,
  openRightModal,
} from "../../../REDUX/actions/rightModal";

/**
 * B4 "Passer la commande": stock of every line (the unit is reserved at
 * creation), buyer and addresses from the project's contact, commercial,
 * total and deposit. A unit sold meanwhile → B4 cas limite (red banner,
 * "Modifier le devis"). On success the partner's cart (the units, linked to
 * the project + quote) opens at step 1 of the order funnel.
 */
const CreateOrderModal = ({ project, quote }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation("common");
  const [preview, setPreview] = useState(null);
  const [units, setUnits] = useState({});
  const [commercial, setCommercial] = useState(null);
  const [failures, setFailures] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    dispatch(fetchQuoteOrderPreview(project.id, quote.id, controller.signal)).then(
      (data) => {
        if (!data) return;
        setPreview(data);
        // Default unit: the quote's own, else the first available one.
        setUnits(
          Object.fromEntries(
            data.lines
              .filter((line) => line.stock === "unit" && line.units.length)
              .map((line) => [line.index, line.units[0].id]),
          ),
        );
        if (data.commercial) {
          setCommercial({ name: data.commercial.name, code: data.commercial.id });
        }
        const known = data.lines.filter((line) => line.unavailable);
        if (known.length) setFailures(known.map((line) => line.unavailable));
      },
    );
    return () => controller.abort();
  }, [dispatch, project.id, quote.id]);

  const close = () => dispatch(closeCenteredModal());
  const failureOf = (line) =>
    failures?.find((failure) => failure.line === line.label) ?? null;

  const handleCreate = async () => {
    setSaving(true);
    const result = await dispatch(
      createQuoteOrder(
        project.id,
        quote.id,
        { units, salesRepId: commercial?.code },
        t,
      ),
    );
    setSaving(false);
    if (result?.unavailable) setFailures(result.unavailable);
    if (!result?.order) return;
    // The funnel opens on that cart (selectedCart; the previous one is cleared).
    dispatch(switchCarts(result.order.cartId));
    close();
    dispatch(closeRightModal());
    navigate("/catalogue");
  };

  const editContact = async () => {
    const row = await dispatch(fetchCustomerRow(preview?.customer?.id));
    if (!row) return;
    close();
    dispatch(openRightModal(<AppCustomerModal customer={row} />));
  };

  const customer = preview?.customer;
  const address = customer?.address;
  const addressBlock = (title, sameAsBilling) => (
    <div className="col-6">
      <div className="p-3 br10 b-1-lightGrey h-100">
        <div className="d-flex justify-content-between mb-2">
          <span className="f14 sBold">{t(title)}</span>
          <button
            type="button"
            className="btn btn-link p-0 f13"
            style={{ color: "#14748e" }}
            onClick={editContact}
          >
            {t("Edit")}
          </button>
        </div>
        <div className="f13 text-ultraBlack">
          <div>{customer?.name || "-"}</div>
          <div>{address?.street || "-"}</div>
          <div>{[address?.zip, address?.city].filter(Boolean).join(" ") || "-"}</div>
          {sameAsBilling ? (
            <div className="text-brightGrey mt-1">{t("Same as billing")}</div>
          ) : (
            <div className="text-brightGrey mt-1">
              {[customer?.email, customer?.phone].filter(Boolean).join(" · ")}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const stockCell = (line) => {
    const failure = failureOf(line);
    if (failure) return <QuotePill tone="red">{failureLabel(failure, t)}</QuotePill>;
    if (line.stock === "service") {
      return <QuotePill tone="grey">{t("Service, no stock")}</QuotePill>;
    }
    if (line.stock !== "unit") return <QuotePill tone="grey">{t("No stock")}</QuotePill>;
    if (!line.units.length) {
      return <QuotePill tone="red">{t("No unit available")}</QuotePill>;
    }
    // Units other than spas (productType 1) are told apart by serial number.
    const labelOf = (unit) =>
      line.productType !== 1 && unit.serialNumber
        ? unit.serialNumber
        : unitLabel(unit, t);
    const selected = line.units.find((unit) => unit.id === units[line.index]);
    if (line.units.length === 1) {
      return <QuotePill tone={unitTone(selected)}>{labelOf(selected)}</QuotePill>;
    }
    const options = line.units.map((unit) => ({ name: labelOf(unit), code: unit.id }));
    return (
      <div style={{ width: 330 }}>
        <Select
          options={options}
          value={options.find((option) => option.code === units[line.index]) ?? null}
          callback={(e) =>
            setUnits((prev) => ({ ...prev, [line.index]: e.value?.code }))
          }
          appendToSelf
        />
      </div>
    );
  };

  const blocked =
    !!failures?.length ||
    !preview ||
    preview.lines.some((line) => line.stock === "unit" && !line.units.length);

  return (
    <div className="p-4 bg-white br10" style={{ width: 820, maxWidth: "90vw" }}>
      <h3 className="text-blue bold f18 quickSand mb-1">{t("Place the order")}</h3>
      <p className="f13 text-brightGrey mb-3">
        {t("Quote")} n°{quote.id} · {customer?.name ?? "-"} · {t("Project")} N°
        {project.number ?? project.id}
        {preview?.organization?.name ? ` · ${preview.organization.name}` : ""}
      </p>
      {!preview ? (
        <div className="d-flex justify-content-center py-5">
          <Spinner />
        </div>
      ) : (
        <>
          {!!failures?.length && (
            <div
              className="p-3 br10 mb-3 f13 sBold"
              style={{ background: "rgba(204,31,8,0.08)", color: "#cc1f08" }}
            >
              {t(
                "A quote line is no longer available. Offer another unit or another model, then resend the quote: the customer will have to accept it again.",
              )}
            </div>
          )}
          <p className="f14 sBold text-ultraBlack mb-2">
            {t(
              failures?.length
                ? "Stock check"
                : "Stock check — the unit (serial number) is reserved on creation",
            )}
          </p>
          {preview.lines.map((line) => (
            <div
              key={line.index}
              className="d-flex align-items-center gap-3 py-2 border-bottom"
            >
              <span className="f14 sBold text-ultraBlack flex-1">{line.label}</span>
              <span className="f14 text-ultraBlack text-end" style={{ width: 110 }}>
                {formatEuro(line.price)}
              </span>
              <div className="d-flex justify-content-end" style={{ width: 330 }}>
                {stockCell(line)}
              </div>
            </div>
          ))}

          <p className="f14 sBold text-ultraBlack mt-4 mb-2">
            {t("Buyer and addresses")}
          </p>
          <div className="row g-3 mb-3">
            {addressBlock("Billing", false)}
            {addressBlock("Delivery", true)}
          </div>

          <div className="d-flex align-items-end gap-3 mb-4">
            <div style={{ width: 280 }}>
              <label className="f14 sBold mb-1">{t("Commercial")}</label>
              <Select
                options={preview.commercials.map((rep) => ({
                  name: rep.name,
                  code: rep.id,
                }))}
                value={commercial}
                callback={(e) => setCommercial(e.value ?? null)}
                appendToSelf
              />
            </div>
            <span className="f12 text-brightGrey pb-2">
              {t("By default: the project's commercial.")}
            </span>
          </div>

          <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
            <span className="f14 sBold text-ultraBlack">
              {t("Total {{amount}} incl. VAT", {
                amount: formatEuro(preview.quote.totalAmount),
              })}
              {" — "}
              {t("Deposit {{rate}} %: {{amount}}", {
                rate: preview.depositRate,
                amount: formatEuro(preview.deposit),
              })}
            </span>
            <div className="d-flex gap-3 ms-auto text-nowrap">
              <Button variant="outline" onClick={close}>
                {t("Cancel")}
              </Button>
              {!!failures?.length && (
                <Button
                  variant="outline"
                  onClick={() =>
                    dispatch(
                      openCenteredModal(
                        <EditQuoteModal project={project} quote={quote} />,
                      ),
                    )
                  }
                >
                  {t("Edit the quote")}
                </Button>
              )}
              <Button
                loading={saving}
                disabled={blocked}
                onClick={saving || blocked ? undefined : handleCreate}
              >
                {t("Place the order")}
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default CreateOrderModal;
